import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import MoaniDisplay from './MoaniDisplay';
import VestingChart from './VestingChart';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Tooltip as MuiTooltip,
  LinearProgress,
  Snackbar,
  Button,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Add as AddIcon,
  CallReceived as IncomingIcon,
  CallMade as OutgoingIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api';

function TrackedWallets() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const [addWalletDialogOpen, setAddWalletDialogOpen] = useState(false);
  const [editWalletDialogOpen, setEditWalletDialogOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [walletDetailsOpen, setWalletDetailsOpen] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [vestingTypeFilter, setVestingTypeFilter] = useState(searchParams.get('vestingType') || '');
  const [activityFilters, setActivityFilters] = useState({
    claimed: false,
    sentOut: false,
    dexSwapped: false
  });
  const [sortBy, setSortBy] = useState('allocation');
  const [sortOrder, setSortOrder] = useState('desc');
  const queryClient = useQueryClient();

  // Debounce search query with longer delay for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500); // Increased from 300ms to 500ms
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch all tracked wallets at once (no server-side filtering)
  const {
    data: walletsData,
    isLoading: walletsLoading
  } = useQuery({
    queryKey: ['allTrackedWallets'],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: '1',
        limit: '10000', // Large limit to get all wallets
        sortBy: 'allocation', // Default server sort
        sortOrder: 'desc'
      });
      
      console.log('🔍 Fetching all wallets with params:', params.toString());
      const response = await api.get(`/allocations/wallets?${params}`);
      console.log('✅ All wallets response:', response.data.wallets?.length, 'wallets loaded');
      return response.data;
    },
    staleTime: 30000, // 30 seconds
  });

  // Get all wallets from the response
  const allWalletsRaw = walletsData?.wallets || [];
  
  // Apply client-side filters with memoization for performance
  const filteredWallets = useMemo(() => {
    console.log('🔍 Filtering wallets:', { search: debouncedSearch, vestingType: vestingTypeFilter, activityFilters });
    
    return allWalletsRaw.filter(wallet => {
    // Activity filters first (fastest checks)
    const hasActiveFilters = activityFilters.claimed || activityFilters.sentOut || activityFilters.dexSwapped;
    if (hasActiveFilters) {
      const dexSwapped = parseFloat(wallet.dexSwapped || 0);
      const sentOut = parseFloat(wallet.totalSentOut || 0);
      const claimed = parseFloat(wallet.claimedFromVesting || 0);
      
      if (activityFilters.claimed && claimed === 0) return false;
      if (activityFilters.sentOut && sentOut === 0) return false;
      if (activityFilters.dexSwapped && dexSwapped === 0) return false;
    }
    
    // Vesting type filter
    if (vestingTypeFilter) {
      // wallet.types is a comma-separated string from GROUP_CONCAT(DISTINCT vestingType)
      const walletVestingTypes = wallet.types ? wallet.types.split(',').map(t => t.trim()) : [];
      const hasVestingType = walletVestingTypes.includes(vestingTypeFilter);
      if (!hasVestingType) return false;
    }
    
    // Search filter (most expensive, do last)
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      const matchesSearch = 
        (wallet.name && wallet.name.toLowerCase().includes(searchLower)) ||
        (wallet.email && wallet.email.toLowerCase().includes(searchLower)) ||
        (wallet.walletAddress && wallet.walletAddress.toLowerCase().includes(searchLower));
      
      if (!matchesSearch) return false;
    }
    
    return true;
    });
  }, [allWalletsRaw, debouncedSearch, vestingTypeFilter, activityFilters]);

  // Apply client-side sorting for new fields with memoization
  const allWallets = useMemo(() => {
    return [...filteredWallets].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'sentOut':
        aValue = parseFloat(a.totalSentOut || 0);
        bValue = parseFloat(b.totalSentOut || 0);
        break;
      case 'received':
        aValue = parseFloat(a.totalReceived || 0);
        bValue = parseFloat(b.totalReceived || 0);
        break;
      case 'claimed':
        aValue = parseFloat(a.claimedFromVesting || 0);
        bValue = parseFloat(b.claimedFromVesting || 0);
        break;
      case 'vested':
        aValue = parseFloat(a.totalVestedAmount || 0);
        bValue = parseFloat(b.totalVestedAmount || 0);
        break;
      case 'dexSwapped':
        aValue = parseFloat(a.dexSwapped || 0);
        bValue = parseFloat(b.dexSwapped || 0);
        break;
      default:
        // For server-side sorting (allocation, name, etc.), keep original order
        return 0;
    }
    
    if (sortOrder === 'desc') {
      return bValue - aValue;
    } else {
      return aValue - bValue;
    }
    });
  }, [filteredWallets, sortBy, sortOrder]);

  // Fetch available vesting types for filter
  const { data: vestingTypes = [] } = useQuery({
    queryKey: ['vestingTypes'],
    queryFn: async () => {
      const response = await api.get('/allocations/vesting-types');
      return response.data;
    },
    staleTime: 300000, // 5 minutes
  });

  // Fetch summary totals based on selected vesting type
  const { data: summaryData, isLoading: summaryLoading, error: summaryError } = useQuery({
    queryKey: ['walletsSummary', vestingTypeFilter],
    queryFn: async () => {
      console.log('🔍 Fetching wallet summary for:', vestingTypeFilter || 'All Types');
      const params = new URLSearchParams();
      if (vestingTypeFilter) {
        params.append('vestingType', vestingTypeFilter);
      }
      const response = await api.get(`/allocations/wallets-summary?${params}`);
      console.log('✅ Summary data received:', response.data);
      return response.data;
    },
    staleTime: 60000, // 1 minute
    retry: 3,
    onError: (error) => {
      console.error('❌ Summary fetch error:', error);
      showToast('Failed to load summary data', 'error');
    }
  });

  // Fetch wallet details when selected
  const { data: walletDetails, isLoading: walletDetailsLoading } = useQuery({
    queryKey: ['walletDetails', selectedWallet?.id],
    queryFn: async () => {
      if (!selectedWallet?.id) return null;
      const response = await api.get(`/allocations/wallets/${selectedWallet.id}`);
      return response.data;
    },
    enabled: !!selectedWallet?.id && walletDetailsOpen
  });

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToast(prev => ({ ...prev, open: false }));
  };

  const handleViewWallet = (wallet) => {
    setSelectedWallet(wallet);
    setWalletDetailsOpen(true);
  };

  const handleAddToTracked = (address) => {
    setSelectedAddress(address);
    setAddWalletDialogOpen(true);
  };

  const addToTrackedMutation = useMutation({
    mutationFn: async (walletData) => {
      const response = await api.post('/allocations/wallets/add', walletData);
      return response.data;
    },
    onSuccess: () => {
      showToast('Wallet added to tracked list successfully', 'success');
      setAddWalletDialogOpen(false);
      setSelectedAddress('');
      queryClient.invalidateQueries(['allTrackedWallets']);
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to add wallet', 'error');
    }
  });

  const updateWalletMutation = useMutation({
    mutationFn: async ({ walletId, updates }) => {
      const response = await api.put(`/allocations/wallets/${walletId}`, updates);
      return response.data;
    },
    onSuccess: (data) => {
      showToast(data.message || 'Wallet updated successfully', 'success');
      setEditWalletDialogOpen(false);
      setSelectedWallet(null);
      queryClient.invalidateQueries(['allTrackedWallets']);
      queryClient.invalidateQueries(['walletsSummary']); // Refresh summary too
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to update wallet', 'error');
    }
  });

  const deleteWalletMutation = useMutation({
    mutationFn: async (walletId) => {
      const response = await api.delete(`/allocations/wallets/${walletId}`);
      return response.data;
    },
    onSuccess: () => {
      showToast('Wallet deleted successfully', 'success');
      queryClient.invalidateQueries(['allTrackedWallets']);
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to delete wallet', 'error');
    }
  });

  const confirmAddWallet = () => {
    if (!selectedAddress) return;
    
    addToTrackedMutation.mutate({
      walletAddress: selectedAddress,
      name: `Tracked Wallet ${selectedAddress.slice(0, 8)}`,
      notes: 'Added from transaction analysis',
      vestingType: 'Unknown',
      kycStatus: 'PENDING'
    });
  };

  const handleEditWallet = (wallet) => {
    setSelectedWallet(wallet);
    setEditWalletDialogOpen(true);
  };

  const handleDeleteWallet = (walletId) => {
    if (window.confirm('Are you sure you want to delete this tracked wallet? This will remove all associated data.')) {
      deleteWalletMutation.mutate(walletId);
    }
  };

  const handleVestingTypeChange = (newVestingType) => {
    setVestingTypeFilter(newVestingType);
    updateUrlParams({ vestingType: newVestingType });
  };

  const handleSearchChange = (newSearch) => {
    setSearchQuery(newSearch);
    updateUrlParams({ search: newSearch });
  };

  const updateUrlParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to desc
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handleForceResyncWallet = async (wallet) => {
    if (window.confirm(`Are you sure you want to force resync ${wallet.name || wallet.walletAddress}? This will scan for missing transactions from August 25, 2025 onwards (existing data will be preserved).`)) {
      try {
        showToast(`Starting comprehensive resync for ${formatAddress(wallet.walletAddress)}...`, 'info');
        
        const response = await api.post(`/allocations/wallets/${wallet.id}/force-resync`);
        showToast(response.data.message, 'success');
        
        // Refresh wallet data
        queryClient.invalidateQueries(['allTrackedWallets']);
        
      } catch (error) {
        console.error('Error starting force resync:', error);
        showToast(error.response?.data?.error || 'Failed to start force resync', 'error');
      }
    }
  };


  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  if (walletsLoading && allWallets.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Tracked Wallets
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddWalletDialogOpen(true)}
        >
          Add Wallet
        </Button>
      </Box>

      {/* Summary Overview */}
      {summaryLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={2} sx={{ mb: 3 }}>
          <CircularProgress size={24} sx={{ mr: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading summary...
          </Typography>
        </Box>
      ) : summaryError ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load summary data: {summaryError.message}
        </Alert>
      ) : summaryData ? (
        <Box sx={{ mb: 3 }}>
          {/* Summary Title Row */}
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mb: 2 }}>
            {summaryData.vestingType} Overview ({summaryData.totalWallets} wallets)
          </Typography>
          
          {/* Summary Cards Row */}
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                    <MoaniDisplay amount={summaryData.totalAllocated} isWei={false} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Allocated
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                    <MoaniDisplay amount={summaryData.totalVested} isWei={false} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Vested
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h6" color="info.main" sx={{ fontWeight: 'bold' }}>
                    <MoaniDisplay amount={summaryData.totalCurrentBalance} isWei={false} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Current Balance
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h6" color="error.main" sx={{ fontWeight: 'bold' }}>
                    <MoaniDisplay amount={summaryData.totalSentOut} isWei={false} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Sent Out
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h6" color="warning.main" sx={{ fontWeight: 'bold' }}>
                    <MoaniDisplay amount={summaryData.totalReceived} isWei={false} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Received
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          No summary data available
        </Alert>
      )}

      {/* Search and Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Tracked Wallets ({allWallets.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Vesting Type</InputLabel>
            <Select
              value={vestingTypeFilter}
              onChange={(e) => handleVestingTypeChange(e.target.value)}
              label="Vesting Type"
            >
              <MenuItem value="">All Types</MenuItem>
              {vestingTypes.map((type) => (
                <MenuItem key={type.vestingType} value={type.vestingType}>
                  {type.displayName} ({type.walletCount})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 'fit-content' }}>
              Activity:
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={activityFilters.claimed}
                  onChange={(e) => setActivityFilters(prev => ({ ...prev, claimed: e.target.checked }))}
                  size="small"
                />
              }
              label="Claimed"
              sx={{ mr: 1 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={activityFilters.sentOut}
                  onChange={(e) => setActivityFilters(prev => ({ ...prev, sentOut: e.target.checked }))}
                  size="small"
                />
              }
              label="Sent Out"
              sx={{ mr: 1 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={activityFilters.dexSwapped}
                  onChange={(e) => setActivityFilters(prev => ({ ...prev, dexSwapped: e.target.checked }))}
                  size="small"
                />
              }
              label="DEX Swapped"
            />
          </Box>
          <TextField
            size="small"
            placeholder="Search wallets, email, name..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{ minWidth: 300 }}
          />
        </Box>
      </Box>

      {/* Wallets Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('name')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Wallet
                  {sortBy === 'name' && (
                    sortOrder === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('vested')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Progress
                  {sortBy === 'vested' && (
                    sortOrder === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('claimed')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Claimed
                  {sortBy === 'claimed' && (
                    sortOrder === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('received')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Received
                  {sortBy === 'received' && (
                    sortOrder === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('sentOut')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Sent Out
                  {sortBy === 'sentOut' && (
                    sortOrder === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('dexSwapped')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  DEX Swapped
                  {sortBy === 'dexSwapped' && (
                    sortOrder === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allWallets.map((wallet) => {
              const claimedFromVesting = parseFloat(wallet.claimedFromVesting || 0);
              
              return (
              <TableRow key={wallet.id}>
                <TableCell>
                  <MuiTooltip title={`Status: ${wallet.isSyncing ? 'Syncing...' : wallet.isActive ? 'Active' : 'Inactive'} | Last sync: ${wallet.lastSyncAt ? new Date(wallet.lastSyncAt).toLocaleString() : 'Never'}`}>
                    <Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 'medium',
                          color: wallet.isSyncing ? 'warning.main' : 'success.main'
                        }}
                      >
                        {wallet.name || 'Unnamed Wallet'} 
                        {wallet.allocationCount > 0 && (
                          <Chip 
                            label={wallet.allocationCount} 
                            size="small" 
                            sx={{ 
                              ml: 1, 
                              height: 16, 
                              fontSize: '0.65rem',
                              backgroundColor: 'primary.main',
                              color: 'primary.contrastText'
                            }} 
                          />
                        )}
                      </Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                        {formatAddress(wallet.walletAddress)}
                      </Typography>
                      
                      {/* Current Balance under wallet address */}
                      <Box sx={{ mt: 0.5 }}>
                        <MoaniDisplay 
                          amount={wallet.moaniBalance ? parseFloat(wallet.moaniBalance) : 0} 
                          isWei={false}
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </Box>
                    </Box>
                  </MuiTooltip>
                </TableCell>
                <TableCell sx={{ minWidth: 300 }}>
                  {(() => {
                    const allocation = parseFloat(wallet.totalAllocation || 0);
                    const sentOut = parseFloat(wallet.totalSentOut || 0);
                    const totalReceived = parseFloat(wallet.totalReceived || 0);
                    
                    if (allocation === 0) {
                      // For non-allocated wallets, show their current holdings
                      const currentBalance = parseFloat(wallet.moaniBalance || 0);
                      if (currentBalance === 0) return <Typography variant="caption">No allocation</Typography>;
                      
                      return (
                        <Box sx={{ position: 'relative' }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={100} 
                            color="info"
                            sx={{ 
                              height: 32, 
                              borderRadius: 10,
                              backgroundColor: 'background.paper'
                            }} 
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              position: 'absolute', 
                              top: '50%', 
                              left: 8, 
                              transform: 'translateY(-50%)',
                              fontSize: '0.875rem',
                              fontWeight: 'bold',
                              color: '#000',
                              textShadow: 'none'
                            }}
                          >
                            <MoaniDisplay 
                              amount={currentBalance} 
                              isWei={false}
                              variant="inherit" 
                              component="span"
                            />
                          </Typography>
                        </Box>
                      );
                    }
                    
                    // Use ONLY backend-calculated vested amount - NO FALLBACKS
                    const vestedAmount = parseFloat(wallet.totalVestedAmount || 0);
                    
                    // Progress: vested amount minus net outflow (excluding vesting claims)
                    const externalReceived = totalReceived - claimedFromVesting;
                    const netSentOut = sentOut - externalReceived;
                    const remainingVested = Math.max(0, vestedAmount - netSentOut);
                    const progressValue = vestedAmount > 0 ? Math.min((remainingVested / vestedAmount) * 100, 100) : 0;
                    
                    const displayAmount = remainingVested;
                    
                    let color = progressValue >= 100 ? 'success' : progressValue >= 75 ? 'info' : progressValue >= 50 ? 'warning' : 'error';
                    
                    return (
                      <Box sx={{ position: 'relative' }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={progressValue} 
                          color={color}
                          sx={{ 
                            height: 32, 
                            borderRadius: 10,
                            backgroundColor: 'background.paper'
                          }} 
                        />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            position: 'absolute', 
                            top: '50%', 
                            left: 8, 
                            transform: 'translateY(-50%)',
                            fontSize: '0.875rem',
                            fontWeight: 'bold',
                            color: '#000',
                            textShadow: 'none'
                          }}
                        >
                          <MoaniDisplay 
                            amount={displayAmount} 
                            isWei={false}
                            variant="inherit" 
                            component="span"
                          />
                        </Typography>
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    if (claimedFromVesting === 0) return null;
                    
                    return (
                      <MoaniDisplay 
                        amount={claimedFromVesting} 
                        isWei={false}
                        variant="body2" 
                        color="info.main"
                      />
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    const received = parseFloat(wallet.totalReceived || 0);
                    if (received === 0) return null;
                    
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" color="success.main" sx={{ mr: 0.5 }}>+</Typography>
                        <MoaniDisplay 
                          amount={received} 
                          isWei={false}
                          variant="body2" 
                          color="success.main"
                        />
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    const sentOut = parseFloat(wallet.totalSentOut || 0);
                    const allocation = parseFloat(wallet.totalAllocation || 0);
                    
                    if (sentOut === 0) {
                      return null;
                    }
                    
                    const sentPercentage = allocation > 0 ? (sentOut / allocation * 100) : 0;
                    
                    return (
                      <Box>
                        <MoaniDisplay 
                          amount={sentOut} 
                          isWei={false}
                          variant="body2" 
                          color="error.main"
                          sx={{ '&::before': { content: '"-"' } }}
                        />
                        <Typography variant="caption" color="error.main" display="block">
                          {sentPercentage.toFixed(1)}% of allocation
                        </Typography>
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    const dexSwapped = parseFloat(wallet.dexSwapped || 0);
                    if (dexSwapped === 0) return null;
                    
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <OutgoingIcon sx={{ color: 'info.main', fontSize: 16 }} />
                        <MoaniDisplay 
                          amount={dexSwapped} 
                          isWei={false}
                          variant="body2" 
                          color="info.main"
                        />
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <MuiTooltip title="View wallet details">
                    <IconButton 
                      size="small"
                      onClick={() => handleViewWallet(wallet)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title="Edit wallet information">
                    <IconButton 
                      size="small"
                      onClick={() => handleEditWallet(wallet)}
                    >
                      <EditIcon />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title="Delete tracked wallet">
                    <IconButton 
                      size="small"
                      onClick={() => handleDeleteWallet(wallet.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title={wallet.isSyncing ? 'Syncing...' : 'Resync wallet from Aug 25, 2025'}>
                    <IconButton 
                      size="small"
                      onClick={() => handleForceResyncWallet(wallet)}
                      disabled={wallet.isSyncing}
                      color="warning"
                    >
                      <RefreshIcon />
                    </IconButton>
                  </MuiTooltip>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      

      {/* Wallet Details Dialog - Same as AllocationDashboard */}
      <Dialog 
        open={walletDetailsOpen} 
        onClose={() => setWalletDetailsOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Wallet Details</Typography>
            {selectedWallet && (
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {selectedWallet.walletAddress}
              </Typography>
            )}
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => window.open(`https://snowtrace.io/address/${selectedWallet?.walletAddress}`, '_blank')}
          >
            View on Snowtrace
          </Button>
        </DialogTitle>
        <DialogContent>
          {walletDetailsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : walletDetails ? (
            <Grid container spacing={3}>
              {/* Wallet Info - Full Width */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Wallet Info</Typography>
                    
                    {/* Multi-column layout for wallet info */}
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Name:</strong> {walletDetails.wallet.name || 'N/A'}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Email:</strong> {walletDetails.wallet.email || 'N/A'}
                        </Typography>
                      </Grid>
                      
                      {selectedWallet && (
                        <>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Total Allocation:</strong>
                              <MoaniDisplay 
                                amount={parseFloat(selectedWallet.totalAllocation || 0)} 
                                isWei={false}
                                variant="inherit" 
                                component="span"
                                sx={{ ml: 1 }}
                              />
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Total Vested:</strong>
                              <MoaniDisplay 
                                amount={parseFloat(selectedWallet.totalVestedAmount || 0)} 
                                isWei={false}
                                variant="inherit" 
                                component="span"
                                sx={{ ml: 1 }}
                              />
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Current Balance:</strong>
                              <MoaniDisplay 
                                amount={parseFloat(selectedWallet.moaniBalance || 0)} 
                                isWei={false}
                                variant="inherit" 
                                component="span"
                                sx={{ ml: 1 }}
                              />
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Recent Transactions */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recent Transactions ({walletDetails.transactions?.length || 0})
                    </Typography>
                    <TableContainer sx={{ maxHeight: 400 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Direction</TableCell>
                            <TableCell>Address</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Platform</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {walletDetails.transactions?.map((tx) => {
                            const lpAddress = '0xFCBDd05F764702eC1cb56C6324a45d6472B51F74'.toLowerCase();
                            const isLpTransfer = tx.toAddress?.toLowerCase() === lpAddress;
                            const isOutgoing = tx.fromAddress?.toLowerCase() === selectedWallet?.walletAddress?.toLowerCase();
                            
                            return (
                              <TableRow key={tx.id} sx={{ backgroundColor: isLpTransfer ? 'rgba(255, 193, 7, 0.1)' : 'inherit' }}>
                                <TableCell>
                                  <Typography variant="caption">
                                    {new Date(tx.transactionTimestamp).toLocaleDateString()}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {isOutgoing ? (
                                      <OutgoingIcon sx={{ color: 'error.main', fontSize: 18 }} />
                                    ) : (
                                      <IncomingIcon sx={{ color: 'success.main', fontSize: 18 }} />
                                    )}
                                    <Typography variant="caption" color={isOutgoing ? 'error.main' : 'success.main'}>
                                      {isOutgoing ? 'OUT' : 'IN'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                      {formatAddress(isOutgoing ? tx.toAddress : tx.fromAddress)}
                                    </Typography>
                                    {isLpTransfer && (
                                      <Chip 
                                        label="LP" 
                                        size="small" 
                                        sx={{ 
                                          backgroundColor: 'warning.main', 
                                          color: 'warning.contrastText',
                                          fontSize: '0.7rem'
                                        }} 
                                      />
                                    )}
                                    <MuiTooltip title="Add to tracked wallets">
                                      <IconButton 
                                        size="small"
                                        onClick={() => handleAddToTracked(isOutgoing ? tx.toAddress : tx.fromAddress)}
                                        sx={{ p: 0.5 }}
                                      >
                                        <AddIcon fontSize="small" />
                                      </IconButton>
                                    </MuiTooltip>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={tx.txType || 'TOKEN_TRANSFER'} 
                                    size="small" 
                                    color={
                                      tx.txType === 'DEX_SWAP' ? 'primary' :
                                      tx.txType === 'LP_MINT' || tx.txType === 'LP_BURN' ? 'warning' :
                                      isLpTransfer ? 'warning' : 'default'
                                    }
                                  />
                                </TableCell>
                                <TableCell>{tx.amount || '-'}</TableCell>
                                <TableCell>{tx.platform || '-'}</TableCell>
                                <TableCell>
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => window.open(`https://snowtrace.io/tx/${tx.transactionHash}`, '_blank')}
                                  >
                                    View Tx
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Allocations */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Allocations ({walletDetails.allocations?.length || 0})
                    </Typography>
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Amount</TableCell>
                            <TableCell>Vesting Type</TableCell>
                            <TableCell>Vesting Status</TableCell>
                            <TableCell>CSV Batch</TableCell>
                            <TableCell>Allocation Date</TableCell>
                            <TableCell>Notes</TableCell>
                            <TableCell>Created</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {walletDetails.allocations?.map((allocation, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
                                  {formatNumber(parseFloat(allocation.allocation || 0))} MOANI
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={allocation.vestingType || 'N/A'} 
                                  size="small" 
                                  color="primary"
                                />
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={allocation.vestingStatus || 'No Vesting'} 
                                  size="small" 
                                  color={
                                    allocation.vestingStatus === 'Fully Vested' ? 'success' :
                                    allocation.vestingStatus === 'Not Started' ? 'default' :
                                    allocation.vestingStatus?.includes('% Vested') ? 'info' :
                                    allocation.vestingStatus === 'Calculation Error' ? 'error' :
                                    'default'
                                  }
                                  variant={allocation.vestingStatus === 'No Vesting' ? 'outlined' : 'filled'}
                                />
                              </TableCell>
                              <TableCell>{allocation.csvBatch || '-'}</TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {allocation.allocationDate ? new Date(allocation.allocationDate).toLocaleDateString() : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {allocation.notes || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {new Date(allocation.createdAt).toLocaleDateString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* LP Transfers */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label="LP" 
                        size="small" 
                        sx={{ 
                          backgroundColor: 'warning.main', 
                          color: 'warning.contrastText',
                          fontSize: '0.7rem'
                        }} 
                      />
                      LP Transfers ({walletDetails.transactions?.filter(tx => 
                        tx.txType === 'LP_MINT' || tx.txType === 'LP_BURN'
                      ).length || 0})
                    </Typography>
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Direction</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {walletDetails.transactions?.filter(tx => {
                            return tx.txType === 'LP_MINT' || tx.txType === 'LP_BURN';
                          }).map((tx) => {
                            return (
                              <TableRow key={tx.id} sx={{ backgroundColor: 'rgba(255, 193, 7, 0.1)' }}>
                                <TableCell>
                                  <Typography variant="caption">
                                    {new Date(tx.transactionTimestamp).toLocaleDateString()}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {tx.txType === 'LP_MINT' ? (
                                      <OutgoingIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                                    ) : (
                                      <IncomingIcon sx={{ color: 'info.main', fontSize: 18 }} />
                                    )}
                                    <Typography variant="caption" color={tx.txType === 'LP_MINT' ? 'warning.main' : 'info.main'}>
                                      {tx.txType === 'LP_MINT' ? 'ADD LIQUIDITY' : 'REMOVE LIQUIDITY'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color={tx.txType === 'LP_MINT' ? 'warning.main' : 'info.main'}>
                                    {tx.txType === 'LP_MINT' ? '-' : '+'}{tx.amount || '0'} MOANI
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => window.open(`https://snowtrace.io/tx/${tx.transactionHash}`, '_blank')}
                                  >
                                    View Tx
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Network Connections */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Connected Wallets ({walletDetails.connections?.length || 0})
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                            <TableRow>
                              <TableCell>Address / Name</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Transaction Count</TableCell>
                              <TableCell>Total Value</TableCell>
                              <TableCell>First / Last Transaction</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                          {walletDetails.connections?.map((conn, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                    {formatAddress(conn.targetAddress)}
                                  </Typography>
                                  {(conn.trackedWalletName || conn.knownAddressName) && (
                                    <Typography variant="caption" color="text.secondary">
                                      {conn.trackedWalletName || conn.knownAddressName}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                {conn.trackedWalletName ? (
                                  <Chip label="Tracked Wallet" size="small" color="success" />
                                ) : conn.knownAddressName ? (
                                  <Chip label={conn.addressType || 'Known'} size="small" color="info" />
                                ) : (
                                  <Chip label="Unknown" size="small" color="default" />
                                )}
                              </TableCell>
                              <TableCell>{conn.transactionCount}</TableCell>
                              <TableCell>{conn.totalValue || '-'}</TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {new Date(conn.firstTransactionAt).toLocaleDateString()} - {new Date(conn.lastTransactionAt).toLocaleDateString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => window.open(`https://snowtrace.io/address/${conn.targetAddress}`, '_blank')}
                                >
                                  Snowtrace
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Vesting Charts - Moved to Bottom */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ color: '#00e676', mb: 2 }}>
                  📈 Vesting Schedule Charts (Frontend Updated)
                </Typography>
                <VestingChart 
                  chartData={walletDetails.vestingChartData}
                />
              </Grid>
            </Grid>
          ) : (
            <Typography>No wallet details available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWalletDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Wallet Dialog */}
      <Dialog open={addWalletDialogOpen} onClose={() => setAddWalletDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Tracked Wallet</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Wallet Address"
              value={selectedAddress}
              onChange={(e) => setSelectedAddress(e.target.value)}
              placeholder="0x..."
              helperText="Enter a valid Ethereum/Avalanche wallet address"
              sx={{ mb: 3 }}
            />
            
            <Alert severity="info">
              <Typography variant="body2">
                This wallet will be added with default settings:
                <ul>
                  <li><strong>Name:</strong> Tracked Wallet {selectedAddress ? selectedAddress.slice(0, 8) : '...'}</li>
                  <li><strong>Notes:</strong> Manually added wallet</li>
                  <li><strong>Vesting Type:</strong> Unknown</li>
                </ul>
                You can edit these details after adding the wallet.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddWalletDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              if (!selectedAddress) {
                showToast('Please enter a wallet address', 'error');
                return;
              }
              
              if (!/^0x[a-fA-F0-9]{40}$/.test(selectedAddress)) {
                showToast('Please enter a valid wallet address', 'error');
                return;
              }
              
              addToTrackedMutation.mutate({
                walletAddress: selectedAddress,
                name: `Tracked Wallet ${selectedAddress.slice(0, 8)}`,
                notes: 'Manually added wallet',
                vestingType: 'Unknown'
              });
            }}
            variant="contained"
            disabled={addToTrackedMutation.isLoading || !selectedAddress}
            startIcon={<AddIcon />}
          >
            {addToTrackedMutation.isLoading ? 'Adding...' : 'Add Wallet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Wallet Dialog */}
      <Dialog open={editWalletDialogOpen} onClose={() => setEditWalletDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Tracked Wallet</DialogTitle>
        <DialogContent>
          {selectedWallet && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Wallet Address"
                    value={selectedWallet.walletAddress || ''}
                    onChange={(e) => setSelectedWallet(prev => ({ ...prev, walletAddress: e.target.value }))}
                    sx={{ fontFamily: 'monospace' }}
                    helperText="Changing the address will update all related allocations and transactions"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={selectedWallet.name || ''}
                    onChange={(e) => setSelectedWallet(prev => ({ ...prev, name: e.target.value }))}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={selectedWallet.email || ''}
                    onChange={(e) => setSelectedWallet(prev => ({ ...prev, email: e.target.value }))}
                    type="email"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    value={selectedWallet.notes || ''}
                    onChange={(e) => setSelectedWallet(prev => ({ ...prev, notes: e.target.value }))}
                    multiline
                    rows={3}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>KYC Status</InputLabel>
                    <Select
                      value={selectedWallet.kycStatus || 'PENDING'}
                      onChange={(e) => setSelectedWallet(prev => ({ ...prev, kycStatus: e.target.value }))}
                      label="KYC Status"
                    >
                      <MenuItem value="PENDING">Pending</MenuItem>
                      <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                      <MenuItem value="REJECTED">Rejected</MenuItem>
                      <MenuItem value="NOT_REQUIRED">Not Required</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={selectedWallet.isActive ? 'active' : 'inactive'}
                      onChange={(e) => setSelectedWallet(prev => ({ ...prev, isActive: e.target.value === 'active' }))}
                      label="Status"
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditWalletDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              if (!selectedWallet) return;
              
              // Validate wallet address format if it was changed
              if (selectedWallet.walletAddress && !/^0x[a-fA-F0-9]{40}$/.test(selectedWallet.walletAddress)) {
                showToast('Please enter a valid wallet address', 'error');
                return;
              }
              
              const updates = {
                walletAddress: selectedWallet.walletAddress,
                name: selectedWallet.name,
                email: selectedWallet.email,
                notes: selectedWallet.notes,
                kycStatus: selectedWallet.kycStatus,
                isActive: selectedWallet.isActive
              };
              
              // Check if wallet address was changed and confirm
              const originalWallet = allWallets.find(w => w.id === selectedWallet.id);
              if (originalWallet && selectedWallet.walletAddress !== originalWallet.walletAddress) {
                const confirmed = window.confirm(
                  `Are you sure you want to change the wallet address from ${originalWallet.walletAddress} to ${selectedWallet.walletAddress}?\n\n` +
                  'This will update ALL related allocations and transaction records to use the new address.'
                );
                if (!confirmed) return;
              }
              
              updateWalletMutation.mutate({ walletId: selectedWallet.id, updates });
            }}
            variant="contained"
            disabled={updateWalletMutation.isLoading}
          >
            {updateWalletMutation.isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseToast} 
          severity={toast.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TrackedWallets;
